import {ConfigObjectType} from '@wallaby-ui/core';
import {Formatters, SelectedFilter, useDRPMedicationData, useOrganizations} from '@wallaby-ui/document';
import {useCallback, useMemo} from 'react';

import {useMedicationOptions} from './hospitalizations_detail/HospitalizationPhRecommendationV2';

import type {PhRecPageFilters} from './PhRecommendationListLayout';

type LabelGetter = (id: unknown) => string;

/** Everything the recommendation filter form and its chips need to turn stored ids into labels. */
export type PhRecommendationChipLookups = {
  getOrganizationLabel: LabelGetter;
  getMedicationAtcLabel: LabelGetter;
};

/**
 * The lookups of the recommendation chips alone (departments and the DRP medication ATC groups), for a surface
 * that shows a saved filter of the pharmacotherapyRecommendationList component as chips without rendering the
 * form (the alert tooltips). The list page keeps its own, wider medication options.
 */
export function usePhRecommendationChipLookups(config: ConfigObjectType): PhRecommendationChipLookups {
  const {data: organizations} = useOrganizations(config);
  const {getDRPMedicationData, medications} = useDRPMedicationData(config);
  const {medicationAtcOptions} = useMedicationOptions({medications, getDRPMedicationData, config, selectedMedicationName: undefined});
  const getOrganizationLabel = useCallback<LabelGetter>(
    (id) => organizations.find((w) => String(w.id) === String(id))?.organizationLongName ?? String(id),
    [organizations]
  );
  const getMedicationAtcLabel = useCallback<LabelGetter>(
    (id) => medicationAtcOptions?.find((w) => String(w.id) === String(id))?.label ?? String(id),
    [medicationAtcOptions]
  );
  return {getOrganizationLabel, getMedicationAtcLabel};
}

/**
 * The filter object the "Active filters" chips are checked against: the nested DRP filter is flattened to
 * `drpFilter_<key>` so a chip can name one of its fields. One rule for the list page and the alert tooltips.
 */
export function phRecommendationTableFiltersForChips(recommendation: PhRecPageFilters): Record<string, any> {
  const {drpFilter, ...rest} = recommendation;
  const result: Record<string, any> = {...rest};
  Object.entries(drpFilter ?? {}).forEach(([key, value]) => {
    result[`drpFilter_${key}`] = value;
  });
  return result;
}

/**
 * The chip definitions of the recommendation filters, one per filter, in the field order of the tab panels.
 * Pure: the list page and the alert tooltips call it with their own lookups.
 */
export function buildPhRecommendationSelectedFilters(
  recommendation: PhRecPageFilters,
  formatters: Pick<Formatters, 't' | 'tEnum' | 'formatDate'>,
  lookups: PhRecommendationChipLookups
): SelectedFilter[] {
  const {t, tEnum, formatDate} = formatters;
  const {getOrganizationLabel, getMedicationAtcLabel} = lookups;
  const withTab = (tab: string, items: SelectedFilter[]): SelectedFilter[] => items.map((v) => ({...v, tab}));
  return [
    ...withTab('general', [
      {
        name: 'patientFullName',
        label: t('filter.patient.fullName'),
        value: recommendation.patientFullName,
      },
      {
        name: 'patientIdentificationNumber',
        label: t('filter.patient.identificationNumber'),
        value: recommendation.patientIdentificationNumber,
      },
      {
        name: 'patientSrcId',
        label: t('filter.patient.sourceId'),
        value: recommendation.patientSrcId,
      },
      {
        name: ['encounterStartedFrom', 'encounterStartedTo', 'encounterFloatingShortcutName'],
        label: t('filter.recommendation.encounterDate'),
        value: `${formatDate(recommendation.encounterStartedFrom ?? '')} - ${formatDate(recommendation.encounterStartedTo ?? '')}`,
      },
      {
        name: 'pharmaRecommendationOrganizationIds',
        label: t('filter.recommendation.organizationIds'),
        value: (recommendation.pharmaRecommendationOrganizationIds ?? []).map(getOrganizationLabel).join(', '),
      },
      {
        name: ['pharmaRecommendationDateFrom', 'pharmaRecommendationDateTo', 'pharmaRecommendationFloatingShortcutName'],
        label: t('filter.recommendation.date'),
        value: `${formatDate(recommendation.pharmaRecommendationDateFrom ?? '')} - ${formatDate(
          recommendation.pharmaRecommendationDateTo ?? ''
        )}`,
      },
      {
        name: 'wasPublished',
        label: t('filter.recommendation.wasPublished'),
        value: t(recommendation.wasPublished ? 'global.boolFilter.true' : 'global.boolFilter.false'),
      },
      {
        name: 'riskiness',
        label: t('filter.recommendation.riskiness'),
        value: (recommendation.riskiness ?? []).map((v) => tEnum('Riskiness', v)).join(', '),
      },
      {
        name: 'riskFactorTypes',
        label: t('filter.patient.riskFactorTypes'),
        value: (recommendation.riskFactorTypes ?? []).map((v) => tEnum('RiskFactorTypeEnum', v)).join(', '),
      },
      {
        name: 'summaryAndConclusionSearchString',
        label: t('filter.recommendation.summaryAndConclusionSearchString'),
        value: recommendation.summaryAndConclusionSearchString,
      },
      {
        name: 'hasDrp',
        label: t('filter.recommendation.hasDrp'),
        value: t(recommendation.hasDrp ? 'global.boolFilter.true' : 'global.boolFilter.false'),
      },
      {
        name: ['followUpDateFrom', 'followUpDateTo', 'followUpFloatingShortcutName'],
        label: t('filter.recommendation.followUpDate'),
        value: `${formatDate(recommendation.followUpDateFrom ?? '')} - ${formatDate(recommendation.followUpDateTo ?? '')}`,
      },
      {
        name: 'procedures',
        label: t('filter.recommendation.procedures'),
        value: (recommendation.procedures ?? []).map((v) => tEnum('Procedure', v)).join(', '),
      },
    ]),
    ...withTab('drp', [
      {
        name: ['drpFilter_medicationIds', 'drpFilter_medicationUserInput'],
        label: t('hospitalization.detail.phRec.drp.medicationName'),
        value: recommendation.drpFilter?.medicationUserInput ?? recommendation.drpFilter?.medicationIds?.join(', '),
      },
      {
        name: 'drpFilter_atcHierarchyIds',
        label: t('hospitalization.detail.phRec.drp.medicationAtcId'),
        value: (recommendation.drpFilter?.atcHierarchyIds ?? []).map(getMedicationAtcLabel).join(', '),
      },
      {
        name: 'drpFilter_types',
        label: t('filter.recommendation.drpFilterTypes'),
        value: (recommendation.drpFilter?.types ?? []).map((v) => tEnum('DrpTypeAllRecommendationsListFilter', v)).join(', '),
      },
      {
        name: 'drpFilter_causes',
        label: t('filter.recommendation.drpFilterCauses'),
        value: (recommendation.drpFilter?.causes ?? []).map((v) => tEnum('DrpCause', v)).join(', '),
      },
      {
        name: 'drpFilter_plannedInterventions',
        label: t('filter.recommendation.drpFilterPlannedInterventions'),
        value: (recommendation.drpFilter?.plannedInterventions ?? []).map((v) => tEnum('DrpPlannedIntervention', v)).join(', '),
      },
      {
        name: 'drpFilter_manifestation',
        label: t('filter.recommendation.drpFilterManifestation'),
        value: tEnum('DrpManifestation', recommendation.drpFilter?.manifestation),
      },
      {
        name: 'drpFilter_interventionAcceptances',
        label: t('filter.recommendation.drpFilterInterventionAcceptances'),
        value: (recommendation.drpFilter?.interventionAcceptances ?? []).map((v) => tEnum('DrpInterventionAcceptance', v)).join(', '),
      },
      {
        name: 'drpFilter_interventionNotAcceptedReasons',
        label: t('filter.recommendation.drpFilterInterventionNotAcceptedReasons'),
        value: (recommendation.drpFilter?.interventionNotAcceptedReasons ?? [])
          .map((v) => tEnum('DrpInterventionNotAcceptedReasonAllRecommendationsListFilter', v))
          .join(', '),
      },
      {
        name: 'drpFilter_statuses',
        label: t('filter.recommendation.drpFilterStatuses'),
        value: (recommendation.drpFilter?.statuses ?? []).map((v) => tEnum('DrpStatus', v)).join(', '),
      },
      {
        name: ['drpFilter_statusAssessedOnFrom', 'drpFilter_statusAssessedOnTo', 'drpFilter_statusAssessedOnFloatingShortcutName'],
        label: t('filter.recommendation.drpFilterStatusAssessedOn'),
        value: `${formatDate(recommendation.drpFilter?.statusAssessedOnFrom ?? '')} - ${formatDate(
          recommendation.drpFilter?.statusAssessedOnTo ?? ''
        )}`,
      },
      {
        name: 'drpFilter_notesSearchString',
        label: t('filter.recommendation.drpFilterNotesSearchString'),
        value: recommendation.drpFilter?.notesSearchString,
      },
    ]),
  ];
}

/** The chips of the recommendation filters, memoized over the filter and the lookups. */
export function usePhRecommendationSelectedFilters(
  recommendation: PhRecPageFilters,
  formatters: Formatters,
  lookups: PhRecommendationChipLookups
): SelectedFilter[] {
  return useMemo(() => buildPhRecommendationSelectedFilters(recommendation, formatters, lookups), [recommendation, formatters, lookups]);
}
